import { useMemo, useState } from 'react'
import {
  CartesianGrid,
  Line,
  LineChart,
  XAxis,
  YAxis,
} from 'recharts'

import { Card, CardContent } from '@/components/ui/card'
import { ChartContainer, ChartTooltip } from '@/components/ui/chart'
import { Checkbox } from '@/components/ui/checkbox'
import { MyDatePicker, useWeeklyDatePicker } from '@/components/MyDatePicker'
import { formatNumber, formatPercent, formatUSD } from '@/lib/format-utils'
import {
  formatRoasDayLabel,
  getDayRoasFieldName,
} from '@/components/roi/day-roas-utils'
import { WEEKLY_ROAS_DAYS } from './constants'

const coreChartConfig = {
  profit: {
    label: 'Profit',
    color: 'hsl(var(--chart-1))',
    formatter: (value) => formatUSD(value, 0, 0),
    yAxis: 'right',
  },
  revenues: {
    label: 'Revenues',
    color: 'hsl(var(--chart-2))',
    formatter: (value) => formatUSD(value, 0, 0),
    yAxis: 'right',
  },
  costs: {
    label: 'Costs',
    color: 'hsl(var(--chart-3))',
    formatter: (value) => formatUSD(value, 0, 0),
    yAxis: 'right',
  },
}

const userChartConfig = {
  dnu: {
    label: 'DNU',
    color: 'hsl(var(--chart-4))',
    formatter: (value) => formatNumber(value, 0, 0),
    yAxis: 'right',
  },
  installs: {
    label: 'Installs',
    color: 'hsl(var(--chart-5))',
    formatter: (value) => formatNumber(value, 0, 0),
    yAxis: 'right',
  },
}

const ratioChartConfig = {
  er: {
    label: 'ER',
    color: 'hsl(var(--chart-6))',
    formatter: (value) => formatPercent(value),
    yAxis: 'right',
  },
  cpi: {
    label: 'CPI(DNU)',
    color: 'hsl(var(--chart-7))',
    formatter: (value) => formatUSD(value, 4, 4),
    yAxis: 'right',
  },
  cpiInstalls: {
    label: 'CPI(Installs)',
    color: 'hsl(var(--chart-8))',
    formatter: (value) => formatUSD(value, 4, 4),
    yAxis: 'right',
  },
}

const roasChartConfig = Object.fromEntries(
  WEEKLY_ROAS_DAYS.map((day, index) => {
    const field = getDayRoasFieldName(day)
    return [
      field,
      {
        label: formatRoasDayLabel(day),
        color: `hsl(var(--chart-${(index % 15) + 1}))`,
        formatter: (value) => formatPercent(value),
        yAxis: 'left',
      },
    ]
  }),
)

const chartConfig = {
  ...coreChartConfig,
  ...userChartConfig,
  ...ratioChartConfig,
  ...roasChartConfig,
}

const metricGroups = {
  core: { label: 'Core', metrics: Object.keys(coreChartConfig) },
  users: { label: 'Users', metrics: Object.keys(userChartConfig) },
  ratios: { label: 'Ratios', metrics: Object.keys(ratioChartConfig) },
  roas: { label: 'ROAS', metrics: Object.keys(roasChartConfig) },
}

const mainGroupKeys = ['core', 'users', 'ratios']
const collapsibleGroupKeys = ['roas']

function MetricCheckboxGroup({
  groupKey,
  metrics,
  selectedMetrics,
  setSelectedMetrics,
}) {
  return (
    <div key={groupKey} className="space-y-2">
      <h3 className="text-sm font-semibold">{metricGroups[groupKey].label}</h3>
      <div className="flex flex-wrap gap-4">
        {metrics.map((metricKey) => {
          const metric = chartConfig[metricKey]
          if (!metric) return null
          return (
            <div key={metricKey} className="flex items-center space-x-2">
              <Checkbox
                id={`${groupKey}-${metricKey}`}
                checked={selectedMetrics.includes(metricKey)}
                onCheckedChange={(checked) => {
                  setSelectedMetrics(
                    checked
                      ? [...selectedMetrics, metricKey]
                      : selectedMetrics.filter((m) => m !== metricKey),
                  )
                }}
              />
              <label
                htmlFor={`${groupKey}-${metricKey}`}
                className="text-sm font-medium"
                style={{ color: metric.color }}
              >
                {metric.label}
              </label>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/** 无实绩或不足展示门槛时不在曲线中描点 */
export function normalizeWeeklyRoasChartValue(value) {
  if (value == null || !Number.isFinite(value)) return undefined
  return value
}

export function computeWeeklyChartYAxisDomain(
  data,
  metricKeys,
  { clampMinZero = false } = {},
) {
  const values = []
  for (const row of data) {
    for (const key of metricKeys) {
      const v = row[key]
      if (typeof v === 'number' && Number.isFinite(v)) {
        values.push(v)
      }
    }
  }
  if (values.length === 0) return [0, 1]

  const min = Math.min(...values)
  const max = Math.max(...values)
  const range = max - min
  const padding =
    range > 0 ? range * 0.1 : Math.max(Math.abs(min) * 0.05, 0.02)

  const domainMin = clampMinZero ? Math.max(0, min - padding) : min - padding
  return [domainMin, max + padding]
}

export function buildWeeklyChartPoint(row) {
  const week = row.week
  const days = week?.days || []
  const weekLabel =
    days.length > 0
      ? `${days[0]}~${days[days.length - 1]}`
      : String(week?.id || '')

  const costsVal = row.costs?.costs
  const revenuesVal = row.revenues?.total
  const dnu = row.dnu?.dnu
  const installs = row.costs?.installs

  const point = {
    weekLabel,
    weekId: week?.id,
    dayCount: days.length,
    dnu: typeof dnu === 'number' ? dnu : undefined,
    installs: typeof installs === 'number' ? installs : undefined,
    revenues: typeof revenuesVal === 'number' ? revenuesVal : undefined,
    costs: typeof costsVal === 'number' ? costsVal : undefined,
    profit:
      typeof revenuesVal === 'number' && typeof costsVal === 'number'
        ? revenuesVal - costsVal
        : undefined,
    er: typeof row.er === 'number' ? row.er / 100 : undefined,
    cpi:
      typeof costsVal === 'number' && dnu
        ? costsVal / dnu
        : undefined,
    cpiInstalls:
      typeof costsVal === 'number' && installs
        ? costsVal / installs
        : undefined,
  }

  WEEKLY_ROAS_DAYS.forEach((day) => {
    const field = getDayRoasFieldName(day)
    point[field] = normalizeWeeklyRoasChartValue(row[field])
  })

  return point
}

export function getDefaultWeeklyChartMetrics({
  readableRevenue,
  readableCost,
}) {
  const metrics = []
  if (readableRevenue && readableCost) metrics.push('profit')
  if (readableRevenue) metrics.push('revenues')
  if (readableCost) metrics.push('costs')
  if (metrics.length === 0) metrics.push('dnu')
  return metrics
}

function formatLeftAxisTick(value) {
  return formatPercent(value)
}

function formatRightAxisTick(value, metrics) {
  const hasUsd = metrics.some((key) =>
    ['profit', 'revenues', 'costs', 'cpi', 'cpiInstalls'].includes(key),
  )
  const hasPercent = metrics.includes('er')
  const hasCount = metrics.some((key) => ['dnu', 'installs'].includes(key))

  if (hasUsd && !hasPercent && !hasCount) {
    return formatUSD(value, 0, 0)
  }
  if (hasPercent && !hasUsd && !hasCount) {
    return formatPercent(value)
  }
  if (hasUsd) return formatUSD(value, 0, 0)
  if (hasPercent) return formatPercent(value)
  if (value >= 10000) return `${(value / 10000).toFixed(1)}W`
  return formatNumber(value, 0, 0)
}

export default function WeeklyChart({
  data,
  initialMetrics,
  availableMetrics,
}) {
  const [selectedMetrics, setSelectedMetrics] = useState(initialMetrics)
  const [expandedGroups, setExpandedGroups] = useState({ roas: true })

  const processedData = useMemo(
    () => [...data].map(buildWeeklyChartPoint).reverse(),
    [data],
  )

  const visibleMainGroups = useMemo(
    () =>
      mainGroupKeys
        .map((key) => ({
          key,
          metrics: metricGroups[key].metrics.filter((m) =>
            availableMetrics.includes(m),
          ),
        }))
        .filter((group) => group.metrics.length > 0),
    [availableMetrics],
  )

  const visibleCollapsibleGroups = useMemo(
    () =>
      collapsibleGroupKeys
        .map((key) => ({
          key,
          metrics: metricGroups[key].metrics.filter((m) =>
            availableMetrics.includes(m),
          ),
        }))
        .filter((group) => group.metrics.length > 0),
    [availableMetrics],
  )

  const toggleGroup = (groupKey) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }))
  }

  const leftMetrics = selectedMetrics.filter(
    (key) => chartConfig[key]?.yAxis === 'left',
  )
  const rightMetrics = selectedMetrics.filter(
    (key) => chartConfig[key]?.yAxis === 'right',
  )

  const leftDomain = useMemo(
    () =>
      computeWeeklyChartYAxisDomain(processedData, leftMetrics, {
        clampMinZero: true,
      }),
    [processedData, leftMetrics],
  )
  const rightDomain = useMemo(
    () => computeWeeklyChartYAxisDomain(processedData, rightMetrics),
    [processedData, rightMetrics],
  )

  return (
    <Card className="shadow-none border-none">
      <div className="flex items-center gap-4 flex-wrap px-6 pt-4 pb-2 border-b sm:gap-2">
        <MyDatePicker
          datePickerStore={useWeeklyDatePicker}
          getMaxDate="complete"
          showMaxDate={true}
        />
      </div>
      <div className="px-6 pt-4 space-y-4">
        <div className="flex flex-wrap gap-4">
          {visibleMainGroups.map((group) => (
            <MetricCheckboxGroup
              key={group.key}
              groupKey={group.key}
              metrics={group.metrics}
              selectedMetrics={selectedMetrics}
              setSelectedMetrics={setSelectedMetrics}
            />
          ))}
        </div>
        {visibleCollapsibleGroups.length > 0 && (
          <div className="flex flex-wrap gap-4">
            {visibleCollapsibleGroups.map((group) => {
              const isExpanded = expandedGroups[group.key]
              return (
                <div
                  key={group.key}
                  className={isExpanded ? 'w-full space-y-2' : 'space-y-2'}
                >
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.key)}
                    className="text-sm font-semibold hover:text-primary flex items-center gap-1"
                  >
                    <span>{isExpanded ? '▼' : '▶'}</span>
                    <span>{metricGroups[group.key].label}</span>
                  </button>
                  {isExpanded && (
                    <div className="flex flex-wrap gap-4 w-full">
                      {group.metrics.map((metricKey) => {
                        const metric = chartConfig[metricKey]
                        return (
                          <div
                            key={metricKey}
                            className="flex items-center space-x-2"
                          >
                            <Checkbox
                              id={`${group.key}-${metricKey}`}
                              checked={selectedMetrics.includes(metricKey)}
                              onCheckedChange={(checked) => {
                                setSelectedMetrics(
                                  checked
                                    ? [...selectedMetrics, metricKey]
                                    : selectedMetrics.filter(
                                        (m) => m !== metricKey,
                                      ),
                                )
                              }}
                            />
                            <label
                              htmlFor={`${group.key}-${metricKey}`}
                              className="text-sm font-medium"
                              style={{ color: metric.color }}
                            >
                              {metric.label}
                            </label>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
      <CardContent>
        <ChartContainer
          className="aspect-auto h-[500px] w-full"
          config={chartConfig}
        >
          <LineChart
            data={processedData}
            margin={{ top: 20, right: 50, bottom: 20, left: 50 }}
          >
            <CartesianGrid vertical={false} />
            <XAxis dataKey="weekLabel" tickLine={false} axisLine={false} />
            {leftMetrics.length > 0 && (
              <YAxis
                yAxisId="left"
                domain={leftDomain}
                tickLine={false}
                axisLine={false}
                tickFormatter={formatLeftAxisTick}
              />
            )}
            {rightMetrics.length > 0 && (
              <YAxis
                yAxisId="right"
                orientation="right"
                domain={rightDomain}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) =>
                  formatRightAxisTick(value, rightMetrics)
                }
              />
            )}
            <ChartTooltip
              cursor={false}
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const item = payload[0]?.payload
                  return (
                    <div className="bg-background border p-2 rounded-md shadow-md min-w-[200px]">
                      <p className="font-semibold">{item?.weekLabel}</p>
                      {item?.weekId && (
                        <p className="text-xs text-muted-foreground">
                          {item.weekId} ({item.dayCount} days)
                        </p>
                      )}
                      <div className="space-y-1 mt-2">
                        {payload.map((pld) => {
                          const dataKey = pld.dataKey
                          const config = chartConfig[dataKey]
                          const value = pld.value
                          if (value === undefined || value === null) return null
                          return (
                            <div
                              key={dataKey}
                              className="flex justify-between items-center gap-4"
                            >
                              <div className="flex items-center">
                                <div
                                  className="w-2 h-2 rounded-full mr-2"
                                  style={{ backgroundColor: pld.color }}
                                />
                                <span>{config?.label || dataKey}</span>
                              </div>
                              <span>
                                {config?.formatter
                                  ? config.formatter(value)
                                  : value}
                              </span>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )
                }
                return null
              }}
            />
            {selectedMetrics.map((metricKey) => {
              const metric = chartConfig[metricKey]
              if (!metric) return null
              return (
                <Line
                  key={metricKey}
                  type="monotone"
                  dataKey={metricKey}
                  yAxisId={metric.yAxis}
                  stroke={metric.color}
                  strokeWidth={2}
                  dot={{ fill: metric.color }}
                  activeDot={{ r: 6 }}
                  connectNulls={false}
                />
              )
            })}
          </LineChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}
